import type { Transition, Variants } from "motion/react";

/** Durations in seconds; mirrored by Tailwind `duration-{name}` in tailwind.config.ts. `none` is the reduced-motion jump. */
export const DURATION = {
  none: 0,
  instant: 0.1,
  fast: 0.15,
  base: 0.2,
  moderate: 0.25,
  slow: 0.3,
  entrance: 0.45,
  emphasis: 0.5,
} as const;

/** Cubic-bezier curves; mirrored by Tailwind `ease-{name}`. `sheet` is the prize-game sheet's iOS lift. */
export const EASE = {
  standard: [0.4, 0, 0.2, 1],
  enter: [0.16, 1, 0.3, 1],
  exit: [0.4, 0, 1, 1],
  sheet: [0.32, 0.72, 0, 1],
} as const satisfies Record<string, readonly [number, number, number, number]>;

/** Spring presets for motion/react; pick by role, never inline stiffness/damping. `wobble` is the under-damped cart bump; `plunge`/`rise` are the night scene's elastic sunset and moonrise. */
export const SPRING = {
  dialog: { type: "spring", stiffness: 300, damping: 24 },
  pop: { type: "spring", stiffness: 320, damping: 18 },
  snap: { type: "spring", stiffness: 380, damping: 16 },
  thumb: { type: "spring", stiffness: 400, damping: 35 },
  bounce: { type: "spring", stiffness: 200, damping: 12 },
  press: { type: "spring", stiffness: 620, damping: 30 },
  wobble: { type: "spring", stiffness: 620, damping: 8, mass: 0.5 },
  plunge: { type: "spring", stiffness: 120, damping: 7 },
  rise: { type: "spring", stiffness: 170, damping: 17 },
} as const satisfies Record<string, Transition>;

/** Ready-made tweens for the recurring roles: enter, exit, size change and colour fade. */
export const TRANSITION = {
  enter: { duration: DURATION.slow, ease: EASE.enter },
  exit: { duration: DURATION.fast, ease: EASE.exit },
  size: { duration: DURATION.moderate, ease: EASE.standard },
  fade: { duration: DURATION.base, ease: EASE.standard },
  progress: { duration: DURATION.entrance, ease: EASE.enter },
} as const satisfies Record<string, Transition>;

/** Animatable shadows (`rest` equals `shadow-card`): motion interpolates literal strings, so morphs read them from here. */
export const MORPH_SHADOW = {
  rest: "0 1px 3px rgb(0 0 0 / 0.08)",
  lifted: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
} as const;

/** Animatable radii for shape morphs (a rounded tile flying into a circle). */
export const MORPH_RADIUS = { tile: "14%", circle: "50%" } as const;

/** Delay between siblings of a staggered list, in seconds. */
export const STAGGER = { tight: 0.04, base: 0.06, loose: 0.08 } as const;

/** Shared variant sets (`hidden` → `visible` → `exit`): one dialog recipe, its reduced twin and the list item rise. */
export const VARIANTS = {
  dialog: {
    hidden: { opacity: 0, scale: 0.95, y: 16 },
    visible: { opacity: 1, scale: 1, y: 0, transition: SPRING.dialog },
    exit: { opacity: 0, scale: 0.95, y: 16, transition: TRANSITION.exit },
  },
  dialogReduced: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: TRANSITION.fade },
    exit: { opacity: 0, transition: TRANSITION.exit },
  },
  fadeUp: {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: TRANSITION.enter },
    exit: { opacity: 0, transition: TRANSITION.exit },
  },
} as const satisfies Record<string, Variants>;

/** Central dialog enter/exit; `reduced` keeps the fade only (pass `useReducedMotion()`). */
export function dialogVariants(reduced: boolean | null): Variants {
  return reduced ? VARIANTS.dialogReduced : VARIANTS.dialog;
}

/** Parent variants that reveal children one `step` (seconds, from `STAGGER`) apart after `delay`. */
export function staggerParent(step: number = STAGGER.base, delay = 0): Variants {
  return {
    hidden: {},
    visible: { transition: { staggerChildren: step, delayChildren: delay } },
  };
}

/** CSS `animation-delay` for the `index`-th sibling of a CSS-staggered list (`animate-item-entrance`). */
export function staggerDelay(index: number, step: number = STAGGER.loose): string {
  return `${Math.round(index * step * 1000)}ms`;
}

/** Seasonal night-scene choreography in seconds: daylight hold, the sun's squash before it drops, and the lags to moonrise and nightfall. */
export const SCENE_DURATION = {
  dayHold: 1.1,
  squash: 0.18,
  moonrise: 0.4,
  nightfall: 0.45,
} as const;

/** Prize-game choreography in seconds (wheel spin, pointer tick, bulbs, landing pulse, confetti, foil flakes, auto-scratch). */
export const GAME_DURATION = {
  spin: 5.2,
  tick: 0.14,
  twinkle: 0.56,
  celebrate: 0.3,
  pulse: 0.48,
  confetti: 1.6,
  flake: 0.65,
  flakeSpread: 0.35,
  autoScratch: 1.2,
} as const;

/** Prize-game curves: the spin's hard flick that brakes to a crawl, the pointer knock, the landing pulse, confetti and flakes. */
export const GAME_EASE = {
  spin: [0.1, 0.7, 0.1, 1],
  tick: [0, 0, 0.58, 1],
  pulse: [0.42, 0, 0.58, 1],
  confetti: [0.2, 0.7, 0.3, 1],
  flake: [0.3, 0.6, 0.6, 1],
} as const satisfies Record<string, readonly [number, number, number, number]>;

/** A curve from `EASE` / `GAME_EASE` as a CSS `cubic-bezier()` for the Web Animations API or a style. */
export function cssEase(curve: readonly [number, number, number, number]): string {
  return `cubic-bezier(${curve.join(", ")})`;
}

/** A duration token (seconds) in the milliseconds the Web Animations API and timers take. */
export function toMs(seconds: number): number {
  return Math.round(seconds * 1000);
}
